import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import { useTheme } from '@mui/material/styles';
import { t } from 'i18next';
import React, { CSSProperties } from 'react';
import AutoSizer, { Size } from 'react-virtualized-auto-sizer';
import {
  FixedSizeList as VirtualList,
  ListChildComponentProps,
  ListOnItemsRenderedProps,
} from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';

type ListItemBase = {
  id?: string;
};

interface ActiveItemsVirtualLazyLoadListProps<TItem extends ListItemBase> {
  items: TItem[];
  hasNextPage: boolean;
  loadNextPage: () => void;
  onClickCallBack: (item: TItem) => void;
  activeItemId: string;
  titleProperty: string;
  itemHeight: number;
  itemCountInitial?: number;
  excludeHeightOffset?: boolean;
}

interface ItemProps extends Pick<ListChildComponentProps, 'index' | 'style'> {
  style: CSSProperties;
}

interface InfiniteLoaderChildProps {
  onItemsRendered: (props: ListOnItemsRenderedProps) => void;
  ref: (ref: InstanceType<typeof VirtualList> | null) => void;
}

const ActiveItemsVirtualLazyLoadList = <TItem extends ListItemBase>({
  // Are there more items to load?
  // (This information comes from the most recent API request.)
  hasNextPage,
  // Are we currently loading a page of items?
  // (This may be an in-flight flag in your Redux store for example.)
  // Array of items loaded so far.
  items,
  // Callback function responsible for loading the next page of items.
  loadNextPage,
  activeItemId,
  onClickCallBack,
  titleProperty,
  itemHeight,
  itemCountInitial,
  excludeHeightOffset = false,
}: ActiveItemsVirtualLazyLoadListProps<TItem>) => {
  const theme = useTheme();
  const maxItemCount = 10000;
  const rowsFromBottomToStartLoading = 15;
  // If there are more items to be loaded then add an extra row to hold a loading indicator.
  const itemCount = hasNextPage ? items.length + 1 : items.length;

  // Only load 1 page of items at a time.
  // Pass an empty callback to InfiniteLoader in case it asks us to load more than once.
  const loadMoreItems = hasNextPage
    ? () => {
        loadNextPage();
      }
    : () => {};

  // Every row is loaded except for our loading indicator row.
  const isItemLoaded = (index: number) => {
    return !hasNextPage || index < items.length + 1;
  };

  // Render an item or a loading indicator.
  const Item = ({ index, style }: ItemProps) => {
    const item = items[index];
    const isActive = activeItemId === item?.id;
    let content;
    if (!isItemLoaded(index)) {
      content = t('actions.loading');
    } else {
      const itemRecord = item as Record<string, React.ReactNode> | undefined;
      content = itemRecord?.[titleProperty];
    }
    return (
      <ListItemButton
        selected={isActive}
        onClick={() => onClickCallBack(item)}
        key={index}
        sx={{
          borderRight: isActive ? `2px solid ${theme.palette.primary.main}` : '',
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
        style={style}
      >
        <ListItemText primary={content} />
      </ListItemButton>
    );
  };

  return (
    <AutoSizer>
      {({ height, width }: Size) => (
        <InfiniteLoader
          isItemLoaded={isItemLoaded}
          itemCount={maxItemCount}
          loadMoreItems={loadMoreItems}
          threshold={rowsFromBottomToStartLoading}
        >
          {({ onItemsRendered, ref }: InfiniteLoaderChildProps) => (
            <VirtualList
              className="List"
              height={excludeHeightOffset ? height : height - itemHeight}
              width={width}
              itemCount={Math.max(itemCountInitial || 0, itemCount)}
              itemSize={itemHeight}
              onItemsRendered={onItemsRendered}
              ref={ref}
            >
              {Item}
            </VirtualList>
          )}
        </InfiniteLoader>
      )}
    </AutoSizer>
  );
};
export { ActiveItemsVirtualLazyLoadList };
